import { useState, useEffect, useCallback } from "react";
import "./index.scss";
import { getWindowSize } from "@wavvest/shared-utils";
import { Table, Spin, Alert, Input, Button, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import type { TableProps } from "antd";
import {
  getRiskassessement,
  replaceRiskassessement,
  RiskassessementData,
  questionsContent,
  answersItem,
} from "@/api/custom";

const RiskAssessment: React.FC = () => {
  const { height } = getWindowSize();
  // const { width, height } = getWindowSize();
  // const initHeight = 880;
  // const headerHeighthRadio = 92 / initHeight;

  const [riskassessement, setRiskassessement] = useState<RiskassessementData>({
    questions: [],
  });
  const [editedData, setEditedData] = useState<RiskassessementData>({
    questions: [],
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  useEffect(() => {
    const fetchRiskAssessmentData = async () => {
      try {
        setLoading(true);
        const data = await getRiskassessement();
        setRiskassessement(data);
        setEditedData(data);
      } catch {
        setError("Failed to load risk assessment data");
      } finally {
        setLoading(false);
      }
    };

    fetchRiskAssessmentData();
  }, []);

  const handleInputChange = useCallback(
    (
      questionIndex: number,
      answerIndex: number,
      key: keyof answersItem,
      value: string,
    ) => {
      const updatedQuestions = [...editedData.questions];
      const answer = updatedQuestions[questionIndex].answers[answerIndex];
      answer[key] = value;
      setEditedData({ questions: updatedQuestions });
    },
    [editedData],
  );

  const handleSave = async () => {
    try {
      setLoading(true);
      await replaceRiskassessement(editedData);
      setRiskassessement(editedData);
      message.success("Data saved successfully");
    } catch (error) {
      message.error("Failed to save data");
      console.error("Save error:", error);
    } finally {
      setLoading(false);
      setIsEditing(false);
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      handleSave();
    } else {
      setIsEditing(true);
    }
  };
  const cancelEdit = () => {
    setIsEditing(false);
  };

  const columns: TableProps<answersItem>["columns"] = [
    {
      title: "Id",
      dataIndex: "id",
      key: "id",
      width: "5%",
    },
    {
      title: "Text",
      dataIndex: "content",
      key: "content",
    },
    {
      title: "Growth",
      dataIndex: "growth",
      key: "growth",
      width: "10%",
      render: (text, _record, index) =>
        isEditing ? (
          <Input
            value={text}
            // disabled={!isEditing}
            onChange={(e) =>
              handleInputChange(0, index, "growth", e.target.value)
            }
          />
        ) : (
          text
        ),
    },
    {
      title: "Capital Preservation",
      dataIndex: "capital_preservation",
      key: "capital_preservation",
      width: "15%",
      render: (text, _record, index) =>
        isEditing ? (
          <Input
            value={text}
            // disabled={!isEditing}
            onChange={(e) =>
              handleInputChange(
                0,
                index,
                "capital_preservation",
                e.target.value,
              )
            }
          />
        ) : (
          text
        ),
    },
  ];

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  return (
    <div className="risk_assessment">
      <div
        className="button"
        style={{
          top: (0.1 * height) / 2 - 20,
          right: 40,
        }}
      >
        <Button
          type="primary"
          icon={<EditOutlined />}
          style={{ backgroundColor: "#263238" }}
          onClick={handleEditToggle}
        >
          {isEditing ? "Confirm" : "Customize"}
        </Button>
        {isEditing && (
          <Button
            type="primary"
            icon={<EditOutlined />}
            style={{ backgroundColor: "#263238", marginLeft: 10 }}
            onClick={cancelEdit}
          >
            {"Cancel"}
          </Button>
        )}
      </div>
      <Spin
        spinning={loading}
        tip="Loading data..."
        wrapperClassName="spin-wrapper"
      >
        <div className="riskassessement-list">
          {riskassessement.questions.map(
            (questionContent: questionsContent, questionIndex: number) => {
              const { context, question, answers } = questionContent;
              return (
                <div key={questionIndex} className="content">
                  <div className="name">
                    <div className="key">Context</div>
                    <div className="value">{context}</div>
                  </div>
                  <div className="name">
                    <div className="key">Question</div>
                    <div className="value">{question}</div>
                  </div>
                  <div className="name">
                    <div className="key">Answers</div>
                    <div className="value table">
                      <Table
                        columns={columns}
                        dataSource={answers.map((answer) => ({
                          ...answer,
                          key: answer.id,
                        }))}
                        rowClassName={(_record: answersItem, index: number) =>
                          index % 2 === 0 ? "even-row" : "odd-row"
                        }
                        scroll={{ y: 900 }}
                        pagination={false}
                      />
                    </div>
                  </div>
                </div>
              );
            },
          )}
        </div>
      </Spin>
    </div>
  );
};

export default RiskAssessment;
